import { useCallback, useContext, useEffect, useMemo } from 'react';

import { toast } from 'react-toastify';

import {
  Maybe,
  useCreditApplicationSubmissionsQuery,
  useOnCreditDecisionSelectedSubscription,
} from '../../gql/generated/graphql';

import { useBanks } from '../../hooks/useBanks';
import { DealContext } from '../../libs/DealContext';
import {
  isCreditDecisionDenied,
  isCreditDecisionPending,
  isCreditDecisionSelectable,
} from '../../utils/creditDecisions';

type UseCreditDecisionsProps = {
  isManuallySelectBankChecked: boolean;
};

const useCreditDecisions = ({ isManuallySelectBankChecked }: UseCreditDecisionsProps) => {
  const { deal } = useContext(DealContext);
  // We want all the banks here because is just to display the names.
  const { banks } = useBanks();

  const { data, loading, startPolling, stopPolling, refetch } =
    useCreditApplicationSubmissionsQuery({
      fetchPolicy: 'cache-and-network',
      variables: {
        filter: {
          dealIds: [deal?.id?.toString()],
        },
      },
      onError: () => {
        toast.error('Error fetching credit decisions, please try again later');
      },
    });

  useOnCreditDecisionSelectedSubscription({
    skip: !deal.id,
    variables: {
      dealId: deal.id ?? -1,
    },
    onData: ({ data: subscriptionData }) => {
      if (!subscriptionData.data?.onCreditDecisionSelected) {
        return;
      }

      refetch();
    },
  });

  useEffect(() => {
    if (isManuallySelectBankChecked) {
      stopPolling();
    } else {
      startPolling(5000);
    }
  }, [isManuallySelectBankChecked]);

  const creditDecisions = useMemo(() => {
    const allCreditApplications =
      data?.creditApplicationSubmissions.flatMap((submission) =>
        submission?.credit_applications?.map((app) => ({
          submittedAt: submission.created_at,
          ...app,
        })),
      ) || [];

    // Give me the newest unique credit decisions by provider_lender_id for each application
    const creditApplicationsToCreditDecisions = allCreditApplications.reduce(
      (acc, app) => {
        if (!app) {
          return acc;
        }

        const credit_decisions = app.credit_decisions || [];
        if (!credit_decisions.length) {
          return acc;
        }

        const appId = app.id;
        if (!appId) {
          return acc;
        }

        const providerLenderIdToCreditDecision = credit_decisions.reduce(
          (decisionsAcc, decision) => {
            if (!decision) {
              return decisionsAcc;
            }

            const decisionWithSubmittedAt = {
              ...decision,
              submittedAt: app.submittedAt,
            };

            const { provider_lender_id, created_at } = decisionWithSubmittedAt;
            if (!provider_lender_id || !created_at) {
              return decisionsAcc;
            }

            if (
              !decisionsAcc[provider_lender_id] ||
              (decisionsAcc[provider_lender_id]?.created_at &&
                decisionsAcc[provider_lender_id].created_at < created_at)
            ) {
              // eslint-disable-next-line no-param-reassign
              decisionsAcc[provider_lender_id] = decisionWithSubmittedAt;
            }

            return decisionsAcc;
          },
          {} as Record<string, typeof credit_decisions[number] & { submittedAt: string }>,
        );

        if (providerLenderIdToCreditDecision) {
          acc[appId] = providerLenderIdToCreditDecision;
        }
        return acc;
      },
      {} as {
        [appId: string]: {
          [providerLenderId: string]: NonNullable<
            NonNullable<typeof allCreditApplications[number]>['credit_decisions']
          >[number] & {
            submittedAt: string;
          };
        };
      },
    );

    return (
      Object.values(creditApplicationsToCreditDecisions)
        .flatMap((providerLenderIdToCreditDecision) =>
          Object.values(providerLenderIdToCreditDecision),
        )
        // sort by credit decision status. A -> S -> D
        .sort((a, b) => {
          const equal = 0;
          if (!a || !b) {
            return equal;
          }
          const aApproved = isCreditDecisionSelectable(a);
          const bApproved = isCreditDecisionSelectable(b);
          const aPending = isCreditDecisionPending(a.application_status);
          const bPending = isCreditDecisionPending(b.application_status);
          const aDenied = isCreditDecisionDenied(a.application_status);
          const bDenied = isCreditDecisionDenied(b.application_status);

          // Put all the approvals first before any pendings.
          if (aApproved && !bApproved) {
            return -1;
          }
          if (!aApproved && bApproved) {
            return 1;
          }
          if (aPending && !bPending) {
            return -1;
          }
          if (!aPending && bPending) {
            return 1;
          }
          if (aDenied && !bDenied) {
            return -1;
          }
          if (!aDenied && bDenied) {
            return 1;
          }

          // If any pair is equal to each other
          return equal;
        })
    );
  }, [data?.creditApplicationSubmissions]);

  useEffect(() => {
    if (!creditDecisions?.length) {
      return;
    }

    const shouldKeepPolling = creditDecisions?.some((decision) =>
      isCreditDecisionPending(decision?.application_status),
    );

    if (shouldKeepPolling) {
      startPolling(5000);
      return;
    }

    stopPolling();
  }, [creditDecisions]);

  const getBankNamesByProviderLenderId = useCallback(
    (providerLenderId?: Maybe<string>) => {
      const bank = banks?.find((b) => b.provider_lender_id === providerLenderId);
      return {
        displayName: bank?.display_name,
        name: bank?.name ?? undefined,
      };
    },
    [banks],
  );

  return {
    creditDecisions,
    creditDecisionsLoading: loading,
    getBankNamesByProviderLenderId,
  } as const;
};

export type CreditDecisionWithSubmittedAt = ReturnType<
  typeof useCreditDecisions
>['creditDecisions'][number];

export default useCreditDecisions;
